<template>
  <div
    class="transaction-card"
    :class="{ 'is-payments-open': paymentsOpen }"
    v-click-outside="closePaymentsDropdown"
  >
    <div
      class="transaction-card-status-bar"
      :style="{ backgroundColor: statusColor }"
      @click="$emit('view-details', transaction)"
    >
      <span>{{ statusLabel }}</span>
    </div>

    <div class="transaction-card-body">
      <div class="transaction-card-main" @click="$emit('view-details', transaction)">
        <b-row class="transaction-card-content">
          <b-col cols="12" md="6" class="transaction-card-col">
            <div class="transaction-card-title">
              <strong>C{{ transaction.id }}</strong>
            </div>
            <div class="transaction-card-description">
              <span>{{ openDateLabel }}
                
              </span>
              <span v-if="transaction.t"> - {{ transaction.t }} </span>
            </div>

            <div
              v-if="bookingClientNames.length"
              class="transaction-card-clients"
              :title="bookingClientNamesTitle"
            >
              <span class="text-muted">{{ bookingClientNamesLabel }}</span>
            </div>

            <div class="transaction-card-value">
              <span>{{ valueLabel }}</span>
            </div>
          </b-col>

          <b-col cols="12" md="6" class="transaction-card-col transaction-card-col-payments">
            <div v-if="payments.length > 0" class="transaction-card-payments">
              <strong class="transaction-card-payments-title">Pagamentos</strong>

              <div class="transaction-card-progress">
                <div class="transaction-card-progress-track">
                  <div
                    class="transaction-card-progress-fill"
                    :style="{
                      width: paidPercent + '%',
                      backgroundColor: statusColor,
                    }"
                  ></div>
                </div>
                <span class="transaction-card-progress-label">{{ paidPercent }}%</span>
              </div>

              <div class="transaction-card-paid-copy">
                {{ paidSummaryLabel }}
              </div>

              <div class="transaction-card-payment-meta">
                <div class="transaction-card-payment-counts">
                  <span v-if="confirmedCount > 0" class="is-confirmed">
                    {{ confirmedCount }}
                    {{ confirmedCount == 1 ? 'confirmado' : 'confirmados' }}
                  </span>
                  <span
                    v-if="confirmedCount > 0 && pendingCount > 0"
                    class="transaction-card-count-sep"
                  >·</span>
                  <span v-if="pendingCount > 0" class="is-pending">
                    {{ pendingCount }}
                    {{ pendingCount == 1 ? 'pendente' : 'pendentes' }}
                  </span>
                </div>

                <button
                  type="button"
                  class="transaction-card-see-payments"
                  @click="togglePaymentsDropdown"
                >
                  ver pagamentos
                  <i
                    :class="paymentsOpen ? 'no no-up-arrow' : 'no no-down-arrow'"
                  ></i>
                </button>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <div v-if="paymentsOpen" class="transaction-card-payments-list">
        <div
          v-for="(payment, idx) in payments"
          :key="idx"
          v-html="paymentLabel(payment)"
          class="transaction-card-payment"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'TransactionCard',

  data() {
    return {
      paymentsOpen: false,
      parceledMethods: [
        2, // Cartão de Crédito
        3, // Cartão de Débito
        9033, // Getnet
      ],
    }
  },

  props: {
    transaction: {
      type: Object,
      required: true,
    },
  },

  computed: {
    openDateLabel() {
      if (!this.transaction.d) return '--'
      const date = moment(this.transaction.d)
      return date.isValid() ? date.format('DD/MM/YYYY') : '--'
    },

    agentName() {
      const user = this.transaction.user
      return user && user.name ? user.name : 'não informado'
    },

    valueLabel() {
      return this.formatCurrency(this.transaction.v)
    },

    bookingClientNames() {
      const products = this.transaction.products || []
      const names = []
      const seen = new Set()

      products.forEach((product) => {
        if (!product || product.removed) return
        if (!product.booking) return

        const rawName =
          product.booking.costumer && product.booking.costumer.name
        const abbreviated = this.abbreviatePersonName(rawName)
        if (!abbreviated) return

        const key = abbreviated.toLowerCase()
        if (seen.has(key)) return
        seen.add(key)
        names.push(abbreviated)
      })

      return names
    },

    bookingClientNamesLabel() {
      return this.bookingClientNames.join(' · ')
    },

    bookingClientNamesTitle() {
      return this.bookingClientNames.join(', ')
    },

    payments() {
      return (this.transaction.payments || []).filter(
        (payment) => Number(payment.value) > 0
      )
    },

    validPayments() {
      return this.payments.filter((payment) => this.isPaymentConfirmed(payment))
    },

    confirmedCount() {
      return this.validPayments.length
    },

    pendingCount() {
      return this.payments.length - this.confirmedCount
    },

    totalPaid() {
      return this.validPayments.reduce(
        (acc, payment) => acc + Number(payment.value || 0),
        0
      )
    },

    paidPercent() {
      const total = Number(this.transaction.v || 0)
      if (total <= 0) return 0
      return Math.min(100, Math.round((this.totalPaid / total) * 100))
    },

    paidSummaryLabel() {
      if (this.paidPercent >= 100) return 'Valor totalmente pago'
      if (this.paidPercent <= 0) return 'Nenhum valor confirmado'
      return `${this.paidPercent}% do valor já está pago`
    },

    paymentStatus() {
      if (this.validPayments.length === 0) return 'orcamento'

      const totalPaid = this.validPayments.reduce(
        (acc, payment) => acc + Number(payment.value || 0),
        0
      )
      const finalValue = Number(this.transaction.v || 0)

      if (totalPaid.toFixed(2) === finalValue.toFixed(2)) return 'concluída'
      return 'parcial'
    },

    statusColor() {
      if (this.paymentStatus === 'orcamento') return '#999'
      if (this.paymentStatus === 'concluída') return '#22A06B'
      return '#E67E22'
    },

    statusLabel() {
      if (this.paymentStatus === 'orcamento') return 'cotação'
      if (this.paymentStatus === 'concluída') return 'concluída'
      return 'parcial'
    },
  },

  methods: {
    abbreviatePersonName(name) {
      const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
      if (!parts.length) return ''
      if (parts.length === 1) return parts[0]
      return `${parts[0]} ${parts[parts.length - 1]}`
    },

    togglePaymentsDropdown(event) {
      event.stopPropagation()
      this.paymentsOpen = !this.paymentsOpen
    },

    closePaymentsDropdown() {
      this.paymentsOpen = false
    },

    isPaymentConfirmed(payment) {
      if (
        payment.method_index == 9033 &&
        (!payment.payment_link || payment.open)
      ) {
        return false
      }
      return payment.date && payment.removed !== true && Number(payment.value) > 0
    },

    formatDate(value, format = 'DD/MM/YY, HH[h]mm') {
      if (!value) return '--'
      const date = moment(value)
      return date.isValid() ? date.format(format) : '--'
    },

    paymentLabel(payment) {
      const method = payment.method || '--'

      const details = []
      if (payment.cardFlag !== '') details.push(payment.cardFlag)
      if (
        payment.installments !== '' &&
        this.parceledMethods.indexOf(Number(payment.method_index)) >= 0
      )
        details.push(`${payment.installments}x`)
      const methodDetails = details.length > 0 ? ` (${details.join(' ')})` : ''

      let labelStatus = ''
      if (payment.payment_link !== false) {
        if (payment.open) {
          if (
            moment().isBefore(moment(`${payment.date} ${payment.time}`), 'hour')
          ) {
            labelStatus =
              '<sub class="font-weight-bold text-muted text-primary">aguardando pagamento</sub>'
          } else {
            labelStatus =
              '<sub class="font-weight-bold text-muted text-warning">link expirado</sub>'
          }
        } else {
          labelStatus =
            '<sub class="font-weight-bold text-muted text-success">link pago</sub>'
        }
      } else if (payment.method_index == 9033) {
        labelStatus = '<sub class="font-weight-bold text-muted">sem link</sub>'
      }

      return `${this.formatDate(
        `${payment.date} ${payment.time || '00:00'}`,
        payment.payment_link ? 'DD/MM/YY, HH[h]mm' : 'DD/MM/YY'
      )} | ${method}${methodDetails} | ${this.formatCurrency(
        payment.value
      )} ${labelStatus}`
    },

    formatCurrency(value) {
      return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
  },
}
</script>

<style scoped>
.transaction-card {
  display: flex;
  align-items: stretch;
  border: 1px solid #d7dde3;
  border-radius: calc(0.375rem - 1px) calc(0.375rem - 1px) 0 0;
  background: #fff;
  padding: 0;
  overflow: hidden;
  cursor: default;
}

.transaction-card-status-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 22px;
  padding: 0rem 0.3rem 0 0.5rem;
  color: #fff;
  font-weight: 500;
  text-transform: lowercase;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  line-height: 1;
  cursor: pointer;
}

.transaction-card-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.transaction-card-main {
  cursor: pointer;
}

.transaction-card-content {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0.5rem 1rem;
}

.transaction-card-see-payments {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  margin: 0 0 0 auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: #12576e;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.transaction-card-see-payments i {
  font-size: 12px;
}

.transaction-card-payments-list {
  margin: 0 1rem 0.75rem;
  padding-top: 8px;
  border-top: 1px solid #eef1f4;
  max-height: 220px;
  overflow-y: auto;
}

.transaction-card-payment {
  margin-top: 6px;
  font-size: 14px;
  color: #363636;
}

.transaction-card-payment:first-child {
  margin-top: 0;
}

.transaction-card:hover {
  border-color: #12576e;
}

.transaction-card-title {
  margin-bottom: 8px;
}

.transaction-card-description {
}

.transaction-card-clients {
  min-width: 0;
  max-width: 100%;
  margin-top: 2px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  line-height: 1.3;
  font-size: 13px;
  word-break: break-word;
}

.transaction-card-value {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;

}

.transaction-card-payments {
  width: 100%;
  font-size: 14px;
}

.transaction-card-payments-title {
  display: block;
  margin-bottom: 8px;
}

.transaction-card-progress {
  display: flex;
  align-items: center;
  gap: 10px;
}

.transaction-card-progress-track {
  flex: 1;
  height: 8px;
  min-width: 0;
  overflow: hidden;
  background: #eef1f4;
  border-radius: 999px;
}

.transaction-card-progress-fill {
  height: 100%;
  border-radius: 999px;
}

.transaction-card-progress-label {
  flex-shrink: 0;
  min-width: 38px;
  color: #363636;
  font-size: 13px;
  font-weight: 700;
  text-align: right;
}

.transaction-card-paid-copy {
  margin-top: 8px;
  color: #505050;
  font-size: 13px;
}

.transaction-card-payment-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
}

.transaction-card-payment-counts {
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
}

.transaction-card-payment-counts .is-confirmed {
  color: #22a06b;
}

.transaction-card-payment-counts .is-pending {
  color: #e67e22;
}

.transaction-card-count-sep {
  margin: 0 6px;
  color: #b0b0b0;
  font-weight: 500;
}

.transaction-card-col {
  margin-bottom: 0;
  min-width: 0;
}

@media (max-width: 767.98px) {
  .transaction-card-col:last-child .transaction-card-payments {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #eef1f4;
  }
}

@media (min-width: 768px) {
  .transaction-card-col:last-child {
    display: flex;
    align-items: center;
    border-left: 1px solid #eef1f4;
  }
}
</style>
